// composeLoop — the per-material compose-loop wear, pure.
//
// The host's vocabulary for what an agent compose turn leaves behind on the
// open material: the pending proposals keyed by element (fed to
// MaterialDocument's `annotations`), which element's review is open, and which
// element is mid-draft. The host holds the useState and advances the value
// ONLY through the folds below; every function here is total, touches no I/O,
// and returns a fresh value (the input is never mutated).
//
// It composes with — never redefines — the Editor's material vocabulary
// (`ElementId`, `PendingProposal`, `ElementAnnotation`). `reviewOpenFor` and
// `drafting` are optional because "nothing open / nothing drafting" is the
// common resting case — absent, not a sentinel.

import type { ElementAnnotation } from "../components/Editor/MaterialDocument/MaterialDocument.js";
import type { ElementId, PendingProposal } from "../components/Editor/types.js";

export type ComposeLoop = {
    readonly annotations: ReadonlyMap<ElementId, ElementAnnotation>;
    readonly reviewOpenFor?: ElementId;
    readonly drafting?: ElementId;
};

// The resting loop — no wear yet. The host starts here (and returns here on
// every material switch) and folds forward.
export const emptyLoop: ComposeLoop = { annotations: new Map() };

// Submit a compose turn against `targetId`: attach the pending proposal (with a
// grounding gutter marker derived from the proposal's own evidence, and the
// agent provenance the proposal carries), open that element's review, and clear
// any in-progress draft. Merges over any existing annotation so prior wear on
// OTHER fields is preserved; returns a fresh loop (the input is untouched).
export const loopAfterSubmit = (
    loop: ComposeLoop,
    targetId: ElementId,
    proposal: PendingProposal,
): ComposeLoop => {
    const existing = loop.annotations.get(targetId);
    const next = new Map(loop.annotations);
    next.set(targetId, {
        authoredBy: proposal.authoredBy,
        proposal,
        markers: [
            ...(existing?.markers ?? []),
            { type: "grounding", state: proposal.grounding },
        ],
    });
    // `drafting` omitted ⇒ cleared; review opens at the change.
    return { annotations: next, reviewOpenFor: targetId };
};

// Resolve the review at `elementId`: drop that element's pending proposal (the
// author has decided) and close the review if it was open there. Provenance and
// non-proposal markers on the element are kept — the decision removes the
// PENDING state, not the element's history. `drafting` is carried through.
export const loopAfterReview = (
    loop: ComposeLoop,
    elementId: ElementId,
): ComposeLoop => {
    const existing = loop.annotations.get(elementId);
    const next = new Map(loop.annotations);
    if (existing !== undefined) {
        // Rebuild without `proposal` — no destructure-omit, no cast.
        next.set(elementId, {
            authoredBy: existing.authoredBy,
            markers: existing.markers,
        });
    }
    return {
        annotations: next,
        reviewOpenFor:
            loop.reviewOpenFor === elementId ? undefined : loop.reviewOpenFor,
        drafting: loop.drafting,
    };
};
